import { memo, useMemo } from "react";
import styled from "styled-components";

import { mediaTimelinePosition } from "./mediaTimelineMath";
import { createTimelineSegmentIndex } from "./timelineSegmentIndex";
import { TimelineSegmentsCanvas } from "./TimelineSegmentsCanvas";
import {
  ACTIVE_SEGMENT_FILL_PERCENT,
  SEGMENT_FILL_PERCENT,
  segmentLineOf,
} from "./timelineSegmentPaint";
import type { MediaTimelineSegment, MediaTimelineSegmentTone } from "./timelineTypes";

// Non-interactive span bands inside their own ordered row. The bar owns only the seat —
// clamping (done upstream by `normalizeMediaTimelineSegments`), viewport math and tone paint —
// and never learns what a span means; selection UI for spans stays consumer-owned (a legend,
// a chip row). The whole layer ignores pointer input on purpose: state bands routinely cover
// the entire track, and a band that swallowed clicks would kill click-to-seek.

// Passive bands use a 30% tint so the duration hatch remains readable. `active` lifts one band
// to 45% plus a 1px inset ring in the tone's line color — overlapping bands share edges, and a
// tint step alone does not say where the emphasized span ends. The ring is inset (no layout
// shift) and the active band is raised above its overlapping siblings so a later band cannot
// paint it over. Same emphasis direction as the marker `active` seat (paint here, decide outside).
const SegmentLayer = styled.div`
  position: absolute;
  z-index: 1;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
`;

const SegmentBand = styled.div<{ $tone: MediaTimelineSegmentTone; $active: boolean }>`
  position: absolute;
  top: 0;
  height: 100%;
  background: ${({ theme, $tone, $active }) =>
    `color-mix(in srgb, ${segmentLineOf(theme, $tone)} ${
      $active ? ACTIVE_SEGMENT_FILL_PERCENT : SEGMENT_FILL_PERCENT
    }%, transparent)`};
  ${({ theme, $tone, $active }) => {
    if (!$active) return "";
    const line = segmentLineOf(theme, $tone);
    return `
      z-index: 1;
      box-shadow: inset 0 0 0 1px ${line};
    `;
  }}
`;

export interface TimelineSegmentsProps {
  /** Already normalized (`normalizeMediaTimelineSegments`) — ordered edges inside the media. */
  segments: readonly MediaTimelineSegment[];
  viewportStart: number;
  viewportDuration: number;
  renderMode?: "dom" | "canvas";
}

/**
 * Purely presentational: every position is derived per render from the same
 * `mediaTimelinePosition` the playhead/selection use, so zoom and pan move bands and spans in
 * lockstep. A cached interval index excludes off-viewport bands without creating an
 * array slot per source segment on every pan. Visible bands keep authored paint order.
 */
export const TimelineSegments = memo(function TimelineSegments({
  segments,
  viewportStart,
  viewportDuration,
  renderMode = "dom",
}: TimelineSegmentsProps) {
  const index = useMemo(() => createTimelineSegmentIndex(segments), [segments]);
  if (segments.length === 0) return null;
  if (renderMode === "canvas") {
    return (
      <SegmentLayer aria-hidden="true" data-media-timeline-segments="">
        <TimelineSegmentsCanvas
          index={index}
          viewportStart={viewportStart}
          viewportDuration={viewportDuration}
        />
      </SegmentLayer>
    );
  }
  const visibleSegments = index.query(viewportStart, viewportStart + viewportDuration);
  return (
    <SegmentLayer aria-hidden="true" data-media-timeline-segments="">
      {visibleSegments.map((segment) => (
        <SegmentBand
          key={segment.id}
          $tone={segment.tone}
          $active={segment.active === true}
          data-media-segment-id={segment.id}
          data-media-segment-label={segment.label}
          data-active={segment.active === true ? "true" : "false"}
          style={{
            left: mediaTimelinePosition(segment.startSeconds, viewportStart, viewportDuration),
            right: `calc(100% - ${mediaTimelinePosition(
              segment.endSeconds,
              viewportStart,
              viewportDuration,
            )})`,
          }}
        />
      ))}
    </SegmentLayer>
  );
});
